import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { Breadcrumb } from '../initiatives/Breadcrumb';
import { EcosystemVisual } from './EcosystemVisual';
import type { EcosystemEntry } from '../../data/ecosystem';
import { easeOutStrong } from '../../utils/motion';
type ProfileHeroProps = {
  entry: EcosystemEntry;
  subtitle: string;
  intro: string;
  stage: string;
  primary: {
    label: string;
    href: string;
  };
  secondary: {
    label: string;
    href: string;
  };
};
const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal';

/** Two-column profile hero: introduction left, identity visual right (below on mobile). */
export function ProfileHero({
  entry,
  subtitle,
  intro,
  stage,
  primary,
  secondary
}: ProfileHeroProps) {
  return <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-40 lg:pb-24" data-id="element-964" data-mp="3ij6m8bgrw7ls">
      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: easeOutStrong
    }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-965" data-mp="3eieczi7wi2pi">
        <Breadcrumb items={[{
        label: 'Ecosystem',
        href: '/ecosystem'
      }, {
        label: entry.name
      }]} data-id="element-966" data-mp="n8aign3sx3r9" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-967" data-mp="rci4udmse7mb">
          <div className="lg:col-span-7" data-id="element-968" data-mp="wa9zvgiqa9uq">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-969" data-mp="3vq6we71m7an2">{entry.relationship}</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-6xl text-ink-dark leading-[1.05] tracking-tight" data-id="element-970" data-mp="3vo3zr45yij9d">
              {entry.name}
            </h1>
            <p className="mt-5 font-heading font-semibold text-xl lg:text-2xl text-navy leading-snug" data-id="element-971" data-mp="8jo3wnm00g13">{subtitle}</p>
            <p className="mt-8 text-lg lg:text-xl text-ink-dark leading-relaxed" data-id="element-972" data-mp="de0mywjkbpcb">{intro}</p>
            <p className="mt-4 text-sm text-ink-muted" data-id="element-973" data-mp="3c1o2xd9ktb2k">{stage}</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4" data-id="element-974" data-mp="3gzzcdmvyljk9">
              <a href={primary.href} className={`inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap ${focusRing}`} data-id="element-975" data-mp="o6aizwx296ew">
                {primary.label}
                <ArrowDown size={18} aria-hidden="true" data-id="element-976" data-mp="swr0bs405c6j" />
              </a>
              <a href={secondary.href} className={`inline-flex items-center justify-center gap-2 border border-line text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap ${focusRing}`} data-id="element-977" data-mp="57oj2lda5lck">
                {secondary.label}
                <ArrowRight size={18} aria-hidden="true" data-id="element-978" data-mp="9907inkf40j6" />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 flex justify-center" data-id="element-979" data-mp="1pmhzw5r5hpj7">
            {entry.image ? <EcosystemVisual entry={entry} size="hero" data-id="element-980" data-mp="1knexkt2k1xex" /> : <div className="flex w-full max-w-md aspect-[4/3] items-center justify-center rounded-lg border border-line bg-surface-bg" data-id="element-981" data-mp="1t4xp9gpc4tkn">
                <EcosystemVisual entry={entry} size="hero" decorative data-id="element-982" data-mp="1x8lwtzdyvp5w" />
              </div>}
          </div>
        </div>
      </motion.div>
    </section>;
}